import { consume } from '@lit/context';
import { localized } from '@weavedev/lit-i18next';
import { html, LitElement, nothing, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
  PrintOptionCollapsibleDto,
  PrintOptionCollapsibleStepDto,
} from '@/api-sdk';
import {
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
} from '@/context';
import { baseStyles } from '@/styles';
import {
  getPrintOptionDisplayData,
  isScrolledIntoView,
  scrollStepIntoView,
} from '@/utils';

import '@/components/accordion/fg-accordion';
import '@/components/accordion/fg-accordion-footer';
import '@/components/accordion/fg-accordion-title';
import '@/components/accordion/fg-accordion-title-button';
import '@/components/configurator/step-collapsible/print-option/fg-collapsible-print-option';

@customElement('fg-print-option-stacked')
@localized()
export class FgPrintOptionStacked extends LitElement {
  @property({ type: Object })
  stepData?: PrintOptionCollapsibleStepDto;

  @property({ type: Boolean })
  showValidationMessage = false;

  @property({ type: Boolean })
  scrollToStep = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @state()
  personalisationData?: PersonalisationType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext!: PersonalisationType[];

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
      scrollStepIntoView(this);
      isScrolledIntoView(this);
    }
  }

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  /**
   * Check if the print option has personalisation data
   * If it does, remove the personalisation data for that print option.
   */
  removePersonalisation(code: string | null | undefined) {
    if (code === null || code === undefined) return;
    const selectedPrintOptionCode = code;
    if (!selectedPrintOptionCode) return;

    const personalisationData = personalisation.getDataWithCode(
      this.personalisationContext,
      selectedPrintOptionCode,
    );
    if (!personalisationData) return;

    this.personalisationData = personalisationData;
    personalisation.providerRemoveEvent(this, selectedPrintOptionCode);
  }

  showAccordionContent(stepData: PrintOptionCollapsibleStepDto) {
    if (this.stepData === undefined) return nothing;

    const { layout } = this.globalContext;

    return html`
      ${stepData.printOptions?.map(
        (printOption: PrintOptionCollapsibleDto, index: number) => {
          const { description, a11yTitle, subtitle } =
            getPrintOptionDisplayData(stepData, printOption);

          return html`
            <fg-accordion-wrapper slot="accordion-content">
              <fg-accordion-title
                part=${layout}
                slot="accordion-wrapper-header"
                index=${index}
                title=${printOption.title}
                a11yTitle=${a11yTitle}
                .description=${description}
                .subtitle=${subtitle}
                .editQueryString=${printOption.editQueryString}
                .removeQueryString=${printOption.removeQueryString}
                .cancelQueryString=${printOption.cancelQueryString}
                .collapseQueryString=${printOption.collapseQueryString}
                .open=${printOption.open}
                .validationMessage=${stepData.validationMessage}
                .showValidationMessage=${this.showValidationMessage}
                .valid=${printOption.selectedPrintOption !== null ||
                stepData.unprinted}
                @action-edit=${() =>
                  this.updateComponent(printOption.editQueryString)}
                @action-delete=${() => {
                  this.updateComponent(printOption.removeQueryString);
                  this.removePersonalisation(
                    printOption?.selectedPrintOption?.code,
                  );
                }}
              >
                <span slot="title-button">
                  ${stepData.unprinted
                    ? html`
                        <fg-accordion-title-button
                          .collapseQueryString=${printOption.collapseQueryString}
                        ></fg-accordion-title-button>
                      `
                    : nothing}
                </span>
              </fg-accordion-title>
              ${stepData.valid && !printOption.open && !stepData.unprinted
                ? html`
                    <fg-collapsible-print-option-personalisation
                      slot="accordion-wrapper-body"
                      .printOption=${printOption}
                    ></fg-collapsible-print-option-personalisation>
                  `
                : html`
                    <fg-collapsible-print-option
                      slot="accordion-wrapper-body"
                      index=${index}
                      .filters=${printOption.filters}
                      .open=${printOption.open}
                      .printOption=${printOption}
                      .totalOptions=${stepData.printOptions.length}
                      .unprintedQueryString=${printOption.unprintedQueryString}
                      .valid=${stepData.valid}
                    ></fg-collapsible-print-option>
                  `}
            </fg-accordion-wrapper>
            ${stepData.addPrintOptionQueryString !== null &&
            stepData.addPrintOptionQueryString !== undefined &&
            !stepData.unprinted &&
            index === 0
              ? html`
                  <fg-accordion-footer
                    slot="accordion-footer"
                    .queryString=${stepData.addPrintOptionQueryString}
                    buttonText="actions.addExtraPrintPosition"
                  ></fg-accordion-footer>
                `
              : nothing}
          `;
        },
      )}
    `;
  }

  render() {
    return html`
      <fg-accordion .stepData=${this.stepData}>
        ${this.stepData ? this.showAccordionContent(this.stepData) : nothing}
      </fg-accordion>
    `;
  }

  static styles = [baseStyles];
}
